import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import {
  Box,
  ComponentMultiStyleConfig,
  Menu,
  MenuItem,
  MenuList,
  Text,
  useMultiStyleConfig,
  useOutsideClick,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { SearchDropdownOption } from "../../../lib";
import { hasValue } from "../../../lib/util/common-util";

export type CMSearchAndSelectDropdownMenuProps<T extends string | number> = {
  /**
   * ドロップダウンに表示する選択肢一覧。
   */
  options: SearchDropdownOption<T>[];

  onSelectOption: (value: T) => void;

  /**
   * メニューの最大表示数
   */
  menuShowMaxCount?: number;

  /**
   * 検索結果が存在しない場合、メニューに表示する文字列
   */
  searchResultNone: string;

  // メニューの開閉設定
  isMenuOpen: boolean;
  onMenuClose: () => void;
} & ComponentStyleProps;

/**
 * テキスト入力の親コンポーネントから呼び出される想定。
 * Contentに設定されたデータを元に、メニュー表示を行う。
 *
 * メニュー項目選択後にメニューを閉じて、選択項目を親コンポーネントに返却する。
 */
export const CMSearchAndSelectDropdownMenu = <T extends string | number>({
  options,

  onSelectOption,

  menuShowMaxCount = 5,

  searchResultNone,

  isMenuOpen,
  onMenuClose,

  sx,
  ...rest
}: CMSearchAndSelectDropdownMenuProps<T>) => {
  const styles = useMultiStyleConfig("CMSearchAndSelectDropdownMenuStyles", {});

  const optionsShown = useMemo(() => {
    if (options.length <= menuShowMaxCount) return options;
    return options.slice(0, menuShowMaxCount);
  }, [options, menuShowMaxCount]);

  // メニュー表示状態で、メニュー項目以外を選択した場合はメニューを閉じる
  const ref = React.useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      if (isMenuOpen) {
        onMenuClose();
      }
    },
  });

  return (
    <Box sx={{ ...styles.container, ...sx }} {...rest}>
      <Menu isOpen={isMenuOpen}>
        <MenuList ref={ref} sx={styles.menu}>
          {optionsShown.length <= 0 ? (
            <MenuItem disabled>
              <Text sx={{ ...styles.itemBase, ...styles.notFoundMessage }}>
                {searchResultNone}
              </Text>
            </MenuItem>
          ) : (
            optionsShown.map((option: SearchDropdownOption<T>, index) => (
              <MenuItem
                key={index}
                onClick={() => onSelectOption(option.value)}
              >
                {hasValue(option.headingLabel) && (
                  <Text sx={{ ...styles.itemBase, ...styles.menuItemId }}>
                    {option.headingLabel}
                  </Text>
                )}
                <Text sx={{ ...styles.itemBase, ...styles.menuItemName }}>
                  {option.mainLabel}
                </Text>
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

/**
 * スタイルの設定
 */
export const CMSearchAndSelectDropdownMenuStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "menu",
    "itemBase",
    "notFoundMessage",
    "menuItemId",
    "menuItemName",
  ],
  baseStyle: {
    container: {},
    menu: {
      // pl: "5px",
      w: "375px",
    },

    itemBase: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: "15px",
    },

    notFoundMessage: {
      w: "360px",
      color: "gray.400",
    },

    menuItemId: {
      w: "80px",
      color: "gray.400",
    },
    menuItemName: {
      w: "270px",
      color: "gray.900",
    },
  },
};
